import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { useApolloClient } from '@apollo/client';
import { Slideout } from '../slideout';
import EditTeam from './EditTeam';
import { useTeamInfo } from '../../team-id-context';
import { usePermissions } from '../../auth';
import { useUserDealRestrictions } from '../../auth/user-restrictions';
import { useDocumentTeamPermissions } from '../../pages/document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { useDealRoles } from '../../shared/hooks/useDealRoles/useDealRoles';
import { useDealId } from '../../deal-id-context';
import CounterpartyPanel from './CounterpartyPanel';
import { TeamMember, User } from '../document-wizard/document-editor/DocumentEditorTypes';
import styles from './styles.module.scss';

interface ManageParticipantsLayoutProps {
  isAdmin: boolean;
  isCreator: boolean;
  hasInviteSendingPermission: boolean;
  isDealShared: boolean;
  counterpartyTeam: {
    teamMembers: TeamMember[];
    adminUser: {
      id: number;
    };
  };
  dealMembers: User[];
  sameSideMembers: User[];
  isDealAccepted: boolean;
  counterpartyInvitedEmail: string;
  counterpartyAdminUserId: string;
  refetchDealData: () => Promise<void>;
  onShowInviteCounterpartyModal: () => void;
  onShowSubscriptionRequiredModal: () => void;
}

interface ManageParticipantsLayoutContainerProps {
  isDealDataLoading: boolean;
  dealData: any;
  refetchDealData: () => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
  onShowInviteCounterpartyModal: () => void;
  onShowSubscriptionRequiredModal: () => void;
}

const ManageParticipantsLayoutContainer = ({
  isDealDataLoading,
  dealData,
  refetchDealData,
  isOpen,
  onClose,
  onShowInviteCounterpartyModal,
  onShowSubscriptionRequiredModal,
}: ManageParticipantsLayoutContainerProps) => {
  const teamId = useTeamInfo().teamId;
  const dealId = useDealId();
  const apolloClient = useApolloClient();
  const { isAdmin } = usePermissions();
  const { isCreator, isDealRolesLoading } = useDealRoles(dealId);
  const { hasInviteSendingPermission } = useDocumentTeamPermissions({ teamId, apolloClient });

  const counterpartyInvitedEmail = useMemo(() => {
    return dealData?.getDealById?.receiverEmail;
  }, [dealData?.getDealById?.receiverEmail]);

  const counterpartyTeam = useMemo(() => {
    return isCreator ? dealData?.getDealById.receiverTeam : dealData?.getDealById.team;
  }, [dealData?.getDealById.receiverTeam, dealData?.getDealById.team, isCreator]);

  if (isDealRolesLoading || isDealDataLoading || !dealData?.getDealById) {
    return null; // Don't show loading in slideout
  }

  const isDealAccepted = dealData?.getDealById?.receiverAcceptedInvitationDate;
  const invitationSentBy = dealData?.getDealById?.invitationSentBy;
  const dealMembers = dealData?.getDealById?.members;
  const sameSideMembers = dealData?.getDealById?.sameSideMembers;

  return (
    <Slideout isOpen={isOpen} onClose={onClose} title="Manage People" width="large">
      <ManageParticipantsLayout
        isAdmin={isAdmin}
        hasInviteSendingPermission={hasInviteSendingPermission}
        isCreator={isCreator}
        isDealShared={!!counterpartyTeam}
        counterpartyTeam={counterpartyTeam}
        dealMembers={dealMembers}
        sameSideMembers={sameSideMembers}
        isDealAccepted={isDealAccepted}
        counterpartyInvitedEmail={counterpartyInvitedEmail}
        counterpartyAdminUserId={isCreator ? counterpartyTeam?.adminUser?.id : invitationSentBy?.id}
        refetchDealData={refetchDealData}
        onShowInviteCounterpartyModal={onShowInviteCounterpartyModal}
        onShowSubscriptionRequiredModal={onShowSubscriptionRequiredModal}
      />
    </Slideout>
  );
};

const ManageParticipantsLayout = ({
  isAdmin,
  isCreator,
  hasInviteSendingPermission,
  isDealShared,
  counterpartyTeam,
  dealMembers,
  sameSideMembers,
  isDealAccepted,
  counterpartyInvitedEmail,
  counterpartyAdminUserId,
  refetchDealData,
  onShowInviteCounterpartyModal,
  onShowSubscriptionRequiredModal,
}: ManageParticipantsLayoutProps) => {
  const [isCounterpartyTabActive, setIsCounterpartyTabActive] = useState(true);
  const { canInviteCounterparty } = useUserDealRestrictions();
  const hasCreatorInvitePermission = isCreator && hasInviteSendingPermission;

  // Everyone on the deal who is not on the viewer's own side. Belonging to the
  // other TEAM is not enough: someone can be a member of the creator's team and
  // still act for the counterparty on a given deal, and listing by membership
  // alone showed them on both sides at once.
  const counterpartyMembersWithAccessToDeal = useMemo(() => {
    const dealMembersIds = dealMembers?.map((member: User) => member.id) ?? [];
    const ownSideIds = new Set<string>((sameSideMembers ?? []).map((member: User) => member.id));

    return counterpartyTeam?.teamMembers?.filter(
      (member: TeamMember) =>
        dealMembersIds.includes(member.user.id) && !ownSideIds.has(member.user.id)
    );
  }, [dealMembers, sameSideMembers, counterpartyTeam]);

  return (
    <div className={styles.manageParticipants}>
      <div className={styles.tabToggleContainer}>
        <button
          className={classNames(styles.tabButton, isCounterpartyTabActive && styles.active)}
          onClick={() => setIsCounterpartyTabActive(true)}
        >
          <FormattedMessage id="title.manage.participants.counterparty" defaultMessage="Counterparty" />
        </button>
        <button
          className={classNames(styles.tabButton, !isCounterpartyTabActive && styles.active)}
          onClick={() => setIsCounterpartyTabActive(false)}
        >
          <FormattedMessage id="title.manage.participants.dealTeam" defaultMessage="Your Deal Team" />
        </button>
      </div>
      <div className={classNames(styles.manageParticipants__content, styles.manageTabs)}>
        {isCounterpartyTabActive ? (
          <div
            className={classNames(
              styles.manageParticipants__counterparty,
              isCounterpartyTabActive && styles['manageParticipants__counterparty--show']
            )}
          >
            <CounterpartyPanel
              isAdmin={isAdmin}
              canInviteCounterparty={canInviteCounterparty}
              hasCreatorInvitePermission={hasCreatorInvitePermission}
              isDealShared={isDealShared}
              counterpartyTeam={counterpartyMembersWithAccessToDeal}
              isDealAccepted={isDealAccepted}
              counterpartyInvitedEmail={counterpartyInvitedEmail}
              counterpartyAdminUserId={counterpartyAdminUserId}
              refetchDealData={refetchDealData}
              onShowInviteCounterpartyModal={onShowInviteCounterpartyModal}
              onShowSubscriptionRequiredModal={onShowSubscriptionRequiredModal}
            />
          </div>
        ) : (
          <div
            className={classNames(
              styles.manageParticipants__dealTeam,
              !isCounterpartyTabActive && styles['manageParticipants__dealTeam--show'],
              styles.manageDealTeamWork
            )}
          >
            <EditTeam />
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageParticipantsLayoutContainer;
